import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef } from 'react';
import { ColDef } from 'ag-grid-community';
import Lottie from 'react-lottie';
import clsx from 'clsx';

import loadingSpinner from '~/shared/assets/lotties/loading-spinner.json';

import TablePagination from './TablePagination';

type PossibleSkeletonRow<T> = T & { _isSkeleton?: boolean };

function isSkeletonRow<T>(data: T): data is PossibleSkeletonRow<T> & { _isSkeleton: true } {
  return (
    typeof data === 'object' &&
    data !== null &&
    '_isSkeleton' in data &&
    (data as PossibleSkeletonRow<T>)._isSkeleton === true
  );
}

export default function Table<T>({
  rowData,
  columnDefs,
  defaultColDef,
  pagination,
  onSortChange,
  headerHeight,
  rowHeight,
  loading,
  suppressNoRowsWhileLoading,
  itemsName = 'volumes',
  noResultsText = 'No Results',
  disableClientSort = true,
  // noRowsOverlayComponent,
  onRowClick,
  rowHoverClass,
  getRowClass,
  getRowId,
}: {
  rowData?: T[];
  columnDefs: ColDef<T>[];
  defaultColDef?: ColDef<T>;
  pagination?: {
    perPage: number;
    onChange: (num: number) => void;
    totalPages: number;
    dataCount: number;
    currentPage?: number;
    itemsName?: string;
  };
  onSortChange?: (sort: { field: string; dir: 'asc' | 'desc' }) => void;
  headerHeight?: number;
  rowHeight?: number;
  loading?: boolean;
  suppressNoRowsWhileLoading?: boolean;
  itemsName: string;
  noResultsText?: string;
  disableClientSort?: boolean;
  noRowsOverlayComponent?: React.ComponentType;
  onRowClick?: (data: T) => void;
  rowHoverClass?: string;
  getRowClass?: (data: T) => string;
  getRowId?: (params: { data: T }) => string;
}) {
  const apiRef = useRef<AgGridReact>(null);

  function onGridReady() {
    apiRef?.current?.api.sizeColumnsToFit();
  }
  const {
    perPage,
    onChange: onPageChange,
    totalPages,
    currentPage: controlledPage,
  } = pagination || {};

  const currentPage = controlledPage ?? 0;

  const goToPage = useCallback(
    (page: number) => {
      onPageChange?.(page);
    },
    [onPageChange]
  );

  const rows = useMemo(() => rowData, [rowData]);
  const columns = useMemo(() => {
    if (!disableClientSort) {
      return columnDefs;
    }
    return (columnDefs || []).map((c) => ({ ...c, comparator: () => 0 }));
  }, [columnDefs, disableClientSort]);

  const getRowClassMemoized = useCallback(
    (params: { data: T | undefined }) => {
      if (!params.data) {
        return '';
      }

      // Handle skeleton rows
      if (isSkeletonRow(params.data)) {
        return 'skeleton-row pointer-events-none';
      }

      const baseClass = onRowClick ? `cursor-pointer ${rowHoverClass || 'hover:bg-gray-50'}` : '';
      const customClass = getRowClass ? getRowClass(params.data) : '';
      return `${baseClass} ${customClass}`.trim();
    },
    [onRowClick, rowHoverClass, getRowClass]
  );

  return (
    <div className="flex-1 flex flex-col gap-[10px] relative">
      <main
        className={`ag-theme-alpine flex-1 relative ${onRowClick && rowHoverClass ? rowHoverClass : ''}`}
      >
        <AgGridReact
          ref={(instance) => {
            if (instance) {
              // @ts-expect-error internal ref assignment
              apiRef.current = instance;
            }
          }}
          suppressNoRowsOverlay={!!loading && !!suppressNoRowsWhileLoading}
          noRowsOverlayComponent={suppressNoRowsWhileLoading ? undefined : NoRowsOverlay}
          rowData={rows}
          columnDefs={columns}
          className="w-full h-full"
          onGridReady={onGridReady}
          sortingOrder={['asc', 'desc']}
          suppressMultiSort
          animateRows={false}
          pagination={!!pagination}
          paginationPageSize={perPage}
          headerHeight={headerHeight}
          rowHeight={rowHeight}
          suppressCellFocus={true}
          suppressRowTransform={true}
          defaultColDef={{
            sortable: true,
            icons: {
              sortAscending: '<p class="ag-icon-tree-open" style="transform: rotate(180deg);"/>',
              sortDescending: '<p class="ag-icon-tree-open" style=""/>',
            },
            ...defaultColDef,
          }}
          suppressPaginationPanel={!!pagination}
          onSortChanged={(s) => {
            const v = s.api.getColumnState().find((c) => !!c.sort);
            onSortChange?.({ field: v!.colId, dir: v!.sort! });
          }}
          suppressScrollOnNewData
          alwaysShowHorizontalScroll={true}
          alwaysShowVerticalScroll={true}
          localeText={{ noRowsToShow: noResultsText }}
          onRowClicked={(event) => {
            // Prevent clicks on skeleton rows
            if (onRowClick && event.data && !isSkeletonRow(event.data)) {
              onRowClick(event.data);
            }
          }}
          getRowClass={getRowClassMemoized}
          getRowId={getRowId}
        />
      </main>
      {totalPages ? (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          goToPage={(num) => {
            goToPage(num);
            apiRef?.current?.api.ensureIndexVisible(0);
          }}
          dataCount={pagination?.dataCount || 0}
          perPage={perPage}
          itemsName={itemsName}
        />
      ) : null}
    </div>
  );
}

function BaseLoadingOverlay() {
  return (
    <div
      className={clsx(
        'flex justify-center items-center bg-whit z-10 w-full h-full absolut inset-0 top8 xl:top10 '
      )}
      aria-label="loading"
    >
      <div className="flex flex-1 justify-center items-center pb-8 ">
        <span className="inline-block ">
          <Lottie
            options={{
              loop: true,
              autoplay: true,
              animationData: loadingSpinner,
              rendererSettings: {
                preserveAspectRatio: 'xMidYMid meet',
              },
            }}
            height={164}
            width={164}
            isClickToPauseDisabled
          />
        </span>
      </div>
    </div>
  );
}
function NoRowsOverlay() {
  return (
    <div className="ag-overlay-no-rows-wrapper flex items-center justify-center text-gray-500 text-sm">
      No Results
    </div>
  );
}
